import { CopyOutlined, ThunderboltOutlined } from "@ant-design/icons";
import {
  Alert,
  Button,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Typography,
  message,
} from "antd";
import { useEffect, useMemo, useState } from "react";
import service from "../../../services/specstudioService";
const { Text, Title } = Typography;

export default function SkuGeneratorModal({
  open,
  onCancel,
  products = [],
  productId,
}) {
  const [form] = Form.useForm();
  const [result, setResult] = useState(null);
  const [employeeCode, setEmployeeCode] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) {
      setResult(null);
      setEmployeeCode("");
      form.resetFields();
      form.setFieldsValue({
        product_id: productId ? Number(productId) : undefined,
        task_name: "",
      });
    }
  }, [open, productId, form]);
  const sku = useMemo(
    () =>
      result
        ? `${result.segments.date}${employeeCode}${result.segments.baseSku}${result.segments.taskCode}`
        : "",
    [result, employeeCode],
  );
  const generate = async () => {
    try {
      const values = await form.validateFields();
      setBusy(true);
      const generated = await service.generateSku(values);
      setResult(generated);
      setEmployeeCode(generated.segments.employeeCode);
    } catch (error) {
      if (!error.errorFields)
        message.error(error.response?.data?.message || error.message);
    } finally {
      setBusy(false);
    }
  };
  const copy = async () => {
    await navigator.clipboard.writeText(sku);
    message.success("Đã sao chép SKU");
  };
  const segments = result
    ? [
        { key: "date", label: "Ngày", value: result.segments.date },
        {
          key: "employee",
          label: "Mã nhân viên",
          value: employeeCode,
          auto: result.employeeCodeGenerated,
        },
        { key: "base", label: "Base SKU", value: result.segments.baseSku },
        { key: "task", label: "Viết tắt task", value: result.segments.taskCode },
      ]
    : [];
  return (
    <Modal
      className="specstudio-dialog-modal specstudio-sku-modal"
      open={open}
      onCancel={onCancel}
      footer={null}
      width={650}
      destroyOnClose
    >
      <div className="specstudio-sku-heading">
        <span className="specstudio-sku-icon">
          <ThunderboltOutlined />
        </span>
        <div>
          <Text className="specstudio-eyebrow">SPECSTUDIO · SKU TOOL</Text>
          <Title level={2}>Generate SKU</Title>
          <Text type="secondary">
            Tạo mã nhất quán từ ngày, mã nhân viên, sản phẩm và task.
          </Text>
        </div>
      </div>
      <div className="specstudio-sku-input-panel">
        <Form form={form} layout="vertical">
          <div className="specstudio-sku-input-grid">
            <Form.Item
              name="product_id"
              label="Sản phẩm"
              rules={[{ required: true, message: "Chọn sản phẩm" }]}
            >
              <Select
                size="large"
                showSearch
                optionFilterProp="label"
                placeholder="Chọn sản phẩm"
                options={products.map((p) => ({
                  value: p.id,
                  label: `${p.productCode} — ${p.name}`,
                }))}
              />
            </Form.Item>
            <Form.Item name="task_name" label="Tên task · tùy chọn">
              <Input
                size="large"
                maxLength={100}
                placeholder="VD: T-shirt white wash"
              />
            </Form.Item>
          </div>
          <Button
            type="primary"
            size="large"
            icon={<ThunderboltOutlined />}
            loading={busy}
            onClick={generate}
          >
            Generate SKU
          </Button>
        </Form>
      </div>
      {result && (
        <div className="specstudio-sku-result">
          <div className="specstudio-sku-result-heading">
            <div>
              <Text className="specstudio-eyebrow">GENERATED RESULT</Text>
              <Title level={4}>SKU của bạn</Title>
            </div>
            <Text type="secondary">
              Có thể sửa mã nhân viên nếu được tạo tự động.
            </Text>
          </div>
          <div className="specstudio-sku-segments">
            {segments.map((segment) => (
              <div
                key={segment.key}
                className={`specstudio-sku-segment ${segment.auto ? "auto" : ""}`}
              >
                <Text>{segment.label}</Text>
                {segment.key === "employee" && segment.auto ? (
                  <Input
                    value={employeeCode}
                    maxLength={20}
                    onChange={(e) =>
                      setEmployeeCode(
                        e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""),
                      )
                    }
                  />
                ) : (
                  <strong>{segment.value}</strong>
                )}
              </div>
            ))}
          </div>
          {result.employeeCodeGenerated && (
            <Alert
              className="specstudio-sku-alert"
              type="warning"
              showIcon
              message="Mã nhân viên được tạo tự động. Hãy kiểm tra trước khi sử dụng."
            />
          )}
          <div className="specstudio-sku-full">
            <div>
              <Text type="secondary">FULL SKU</Text>
              <div className="specstudio-sku-full-value">{sku}</div>
            </div>
            <Button icon={<CopyOutlined />} onClick={copy}>
              Copy SKU
            </Button>
          </div>
        </div>
      )}
      <div className="specstudio-sku-modal-foot">
        <Text type="secondary">
          Cấu trúc: YYMMDD · Employee code · Base SKU · Task code
        </Text>
        <Space>
          {result && (
            <Button onClick={() => setResult(null)}>Tạo mã khác</Button>
          )}
          <Button onClick={onCancel}>Đóng</Button>
        </Space>
      </div>
    </Modal>
  );
}
